/**
 * Data plumbing for the Campaign Summary dashboard.
 *
 * TWO FEEDS, deliberately separate:
 *
 *   LIVE      `supervisor:*` sockets → `useSupervisorStore`. Agent status,
 *             in-flight calls. Pushed by the gateway every few seconds (see
 *             ws-gateway `supervisor/broadcaster.ts`) plus an immediate
 *             `supervisor:agent_update` on every status transition.
 *
 *   HISTORY   GET /supervisor/campaign-summary, polled. Answered / abandoned /
 *             ASA / AHT / service level over the trailing window.
 *
 * The reference (KC2) polls BOTH at 2s, firing six socket round-trips and
 * roughly eight queries per supervisor per tick. That is not reproduced: the
 * live half is now pushed rather than polled, and the history half aggregates
 * over `calls` — a materially more expensive query than KC2's pre-rolled
 * 5-minute table — so polling it at 2s would put a scan of the trailing window
 * on Postgres several times a second per connected supervisor. 15s is well
 * inside how fast these numbers actually move.
 *
 * The 1s `tick` is the reference's one genuinely good idea and is kept: call
 * durations are recomputed client-side from `startedAt` so timers advance
 * smoothly between pushes instead of jumping in 3s steps.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../../../api/client.js'
import { getSocket } from '../../../socket/client.js'
import { wireSupervisorEvents } from '../../../socket/supervisor-events.js'
import { useUiStore } from '../../../store/uiStore.js'
import { useSupervisorStore } from '../../../store/supervisorStore.js'
import { publishLive } from './live-store.js'
import type { CampaignOption, CampaignSummaryResponse } from './types.js'

/** How often the aggregate endpoint is re-fetched. */
export const HISTORY_POLL_MS = 15_000

/** Interval-width options, matching the KC2 selector. */
export const INTERVAL_OPTIONS = [
  { label: '30 minutes', value: '30' },
  { label: '60 minutes', value: '60' },
  { label: '12 hours', value: '720' },
]

export const WINDOW_OPTIONS = [
  { label: 'Last 1 hour', value: 1 },
  { label: 'Last 4 hours', value: 4 },
  { label: 'Last 8 hours', value: 8 },
  { label: 'Last 12 hours', value: 12 },
  { label: 'Last 24 hours', value: 24 },
]

const EMPTY_SUMMARY: CampaignSummaryResponse['totals'] = {
  totalCalls: 0,
  totalInbound: 0,
  totalOutbound: 0,
  answeredTotal: 0,
  abandonedTotal: 0,
  abandonedIvr: 0,
  abandonedQueue: 0,
  totalTalkSec: 0,
  avgTalkSec: 0,
  avgHandleSec: 0,
  totalHoldSec: 0,
  avgHoldSec: 0,
  totalAcwSec: 0,
  totalWaitSec: 0,
  avgAnswerSpeedSec: 0,
  maxWaitSec: 0,
  answeredInSl: 0,
  answerRatePct: 0,
  abandonRatePct: 0,
  ivrAbandonRatePct: 0,
  serviceLevelPct: 0,
}

export const EMPTY_RESPONSE: CampaignSummaryResponse = {
  serverTime: '',
  window: { hours: 12, intervalMin: 30, campaignId: null, queueId: null },
  intervals: [],
  agents: [],
  queues: [],
  campaigns: [],
  dispositions: [],
  totals: EMPTY_SUMMARY,
}

/**
 * Subscribe to the supervisor socket room for the lifetime of the component.
 *
 * The ref guard is not defensive padding — React StrictMode runs effects twice
 * in development, and a second `supervisor:subscribe` would inflate the
 * gateway's broadcast refcount for the tenant.
 */
export function useSupervisorFeed(): void {
  const wired = useRef(false)

  useEffect(() => {
    if (wired.current) return
    wired.current = true

    const socket = getSocket()
    const cleanup = wireSupervisorEvents(socket)
    socket.emit('supervisor:subscribe')

    // Re-subscribe after a transport drop: the gateway's room membership and
    // refcount live on the socket, so a reconnect starts from nothing and the
    // feed would go permanently silent without this.
    const onReconnect = (): void => {
      socket.emit('supervisor:subscribe')
    }
    socket.on('connect', onReconnect)

    return () => {
      socket.off('connect', onReconnect)
      socket.emit('supervisor:unsubscribe')
      cleanup()
      wired.current = false
    }
  }, [])
}

/**
 * Feed `live-store` straight from `useSupervisorStore` — the real page's
 * publisher.
 *
 * On the demo dashboard `useDemoOverlay` owns this job (its 1s interval picks
 * demo-or-real and calls `publishLive`). The real page mounts no overlay, so
 * without this hook `live-store` is written by nothing and every live panel
 * renders EMPTY forever — "Offline" badge, "No agents online" — while the
 * socket feed fills `useSupervisorStore` unseen.
 *
 * Subscribed imperatively rather than via `useSupervisorStore(selector)` in
 * render: the whole point of `live-store` is that live data never passes
 * through the page component — a page render re-lays-out the entire grid. This
 * publishes on every store change from BELOW the page, exactly like the
 * overlay's interval did. `publishLive` dedupes on a data signature, so
 * unchanged pushes cost nothing.
 */
export function useSupervisorLiveBridge(): void {
  useEffect(() => {
    const publish = (): void => {
      const s = useSupervisorStore.getState()
      publishLive({ agents: s.agentList, calls: s.liveCalls })
    }
    publish() // paint whatever arrived before mount, don't wait for the next push
    return useSupervisorStore.subscribe(publish)
  }, [])
}

/**
 * A monotonically increasing counter that advances once a second.
 *
 * Components read it purely to re-render; the value itself is meaningless.
 * This is what keeps live call timers ticking between gateway pushes.
 */
export function useSecondTick(): number {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [])
  return tick
}

export interface HistoryState {
  data: CampaignSummaryResponse
  loading: boolean
  error: string | null
  /** Manual refresh, also used by the toolbar's refresh button. */
  refresh: () => void
}

export function useCampaignHistory(params: {
  intervalMin: string
  hours: number
  campaignId: string | null
}): HistoryState {
  const { intervalMin, hours, campaignId } = params
  const addNotification = useUiStore((s) => s.addNotification)

  const [data, setData] = useState<CampaignSummaryResponse>(EMPTY_RESPONSE)
  // Only the FIRST load blocks the UI. Poll ticks refresh in place, because a
  // spinner every 15s over a dashboard someone is reading is worse than
  // briefly-stale numbers.
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  // Errors are notified once per transition into the failed state, not once
  // per poll — a backend outage would otherwise emit a toast every 15s.
  const errored = useRef(false)

  const refresh = useCallback(() => setNonce((v) => v + 1), [])

  useEffect(() => {
    let cancelled = false

    const load = async (): Promise<void> => {
      const query = new URLSearchParams({
        intervalMin,
        hours: String(hours),
      })
      if (campaignId) query.set('campaignId', campaignId)

      try {
        const res = await api.get<CampaignSummaryResponse>(
          `/supervisor/campaign-summary?${query.toString()}`,
        )
        if (cancelled) return
        setData(res)
        setError(null)
        errored.current = false
      } catch (err) {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Unknown error'
        setError(message)
        if (!errored.current) {
          errored.current = true
          addNotification(`Campaign summary failed to load: ${message}`, 'error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    const id = setInterval(() => void load(), HISTORY_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [intervalMin, hours, campaignId, nonce, addNotification])

  return { data, loading, error, refresh }
}

/** Campaign picker options, with the "All Campaigns" sentinel prepended. */
export function useCampaignOptions(): CampaignOption[] {
  const [options, setOptions] = useState<CampaignOption[]>([
    { label: 'All Campaigns', value: '' },
  ])

  useEffect(() => {
    let cancelled = false
    api
      .get<{ campaigns: { id: string; name: string }[] }>(
        '/campaigns/dropdown?voice_only=true',
      )
      .then((res) => {
        if (cancelled) return
        setOptions([
          { label: 'All Campaigns', value: '' },
          ...res.campaigns.map((c) => ({ label: c.name, value: c.id })),
        ])
      })
      // A supervisor without CAMPAIGNS_VIEW still gets the whole dashboard,
      // just without the campaign filter — so a 403 here is not an error worth
      // interrupting them over.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  return options
}
